import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      id: 1,
      question: "What do I need to provide for your services?",
      answer: "You'll only need to share your project name, project description, and logo. Our team handles everything else, including the creation of a custom 3D rotating emoji of your logo."
    },
    {
      id: 2,
      question: "What if I don't want to share sensitive details like the project name or contract address right away?",
      answer: "That's absolutely fine. Our team can begin by setting up everything using placeholder (dummy) information and complete the final configurations once you're ready to share the official details. Your privacy and project security are always our top priority."
    },
    {
      id: 4,
      question: "How long does the setup take?",
      answer: "Your complete Telegram setup will be delivered within 48 hours, including weekends."
    },
    {
      id: 5,
      question: "What if I'm not ready to make the Telegram group public right away?",
      answer: "No problem! We always prepare your Telegram group and keep it private until you decide to make it public. You have full control over when it goes live."
    },
    {
      id: 6,
      question: "When does your service end, and what if I need help afterwards?",
      answer: "Our service concludes once your Telegram group is fully configured and ready to launch. However, if you have any problems with it during the first 2 weeks, we will offer free assistance to make any necessary corrections."
    },
    {
      id: 7,
      question: "What if I need to make changes to the existing bots and configurations?",
      answer: "We've got you covered. You'll receive clear, step-by-step instructions so your moderators can easily update and manage all bots and configurations."
    }
  ];

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section id="faq" className="py-14 px-4 min-h-screen flex flex-col justify-center">
      <div className="max-w-4xl mx-auto w-full">
        <div className="text-center mb-10">
          <h2 className="display-large mb-4">Frequently Asked Questions</h2>
          <p className="body-large">
            Everything you need to know about our Telegram setup services
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className={`
                relative rounded-lg overflow-hidden transition-all duration-300 transform
                ${openFAQ === faq.id 
                  ? 'border-2 border-cyan-400 shadow-lg scale-102' 
                  : 'border-2 border-gray-700 hover:border-cyan-500 hover:scale-101'
                }
              `}
              style={{
                background: openFAQ === faq.id 
                  ? 'linear-gradient(135deg, rgba(0, 218, 240, 0.05), rgba(18, 18, 18, 0.95))' 
                  : 'rgba(18, 18, 18, 0.8)',
                boxShadow: openFAQ === faq.id 
                  ? '0 0 20px rgba(0, 218, 240, 0.3), inset 0 0 20px rgba(0, 218, 240, 0.05)' 
                  : '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
            >
              {/* Animated border effect for expanded state */}
              {openFAQ === faq.id && (
                <div 
                  className="absolute inset-0 rounded-lg pointer-events-none animate-pulse"
                  style={{
                    background: 'linear-gradient(45deg, transparent 30%, rgba(0, 218, 240, 0.1) 50%, transparent 70%)'
                  }}
                />
              )}
              
              <button
                onClick={() => toggleFAQ(faq.id)}
                className={`
                  relative w-full p-5 text-left flex items-center justify-between 
                  transition-all duration-300 z-10
                  ${openFAQ === faq.id 
                    ? 'bg-transparent' 
                    : 'hover:bg-gradient-to-r hover:from-gray-900 hover:to-gray-800'
                  }
                `}
              >
                <h3 className={`
                  text-base font-semibold transition-colors duration-300 pr-4
                  ${openFAQ === faq.id 
                    ? 'text-cyan-400' 
                    : 'text-white group-hover:text-cyan-400'
                  }
                `}>
                  {faq.question}
                </h3>
                <div className="ml-4 flex-shrink-0">
                  <div 
                    className={`
                      p-1 rounded-full transition-all duration-300
                      ${openFAQ === faq.id 
                        ? 'bg-cyan-400 bg-opacity-20' 
                        : 'hover:bg-cyan-400 hover:bg-opacity-10'
                      }
                    `}
                  >
                    {openFAQ === faq.id ? (
                      <ChevronUp 
                        size={24} 
                        style={{ 
                          color: '#00DAF0',
                          filter: 'drop-shadow(0 0 4px rgba(0, 218, 240, 0.6))'
                        }} 
                      />
                    ) : (
                      <ChevronDown 
                        size={24} 
                        style={{ color: '#00DAF0' }} 
                      />
                    )}
                  </div>
                </div>
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  openFAQ === faq.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-5 pb-5 relative">
                  {/* Subtle glow line separator */}
                  <div 
                    className="w-full h-px mb-4"
                    style={{
                      background: 'linear-gradient(90deg, transparent, #00DAF0, transparent)',
                      boxShadow: '0 0 10px rgba(0, 218, 240, 0.4)'
                    }}
                  />
                  <p className="body-small text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CSS animations are handled inline */}
      </div>
    </section>
  );
};

export default FAQSection;